// #region Imports

import chroma from 'chroma-js';
import type { CssColor } from '../../../Utils/CssColor';
import type { ThemePalette } from '../../../Utils/ThemePalette';
import type { ThemeScheme } from '../../../Utils/ThemeScheme';
import type { IThemeGeneratorStrategy } from '../../interfaces/IThemeGeneratorStrategy';

// #endregion

/**
 * Abstract theme generator strategy that operates in the **HSL** (Hue, Saturation, Lightness)
 * color space.
 *
 * @remarks
 * HSL is a cylindrical color model widely used in CSS and web design tooling.
 * Unlike LCH it is not perceptually uniform, but it provides a familiar and
 * lightweight alternative for palette generation.
 *
 * Concrete themes that prefer HSL-based generation extend this class.
 *
 * @public
 * @abstract
 */
export abstract class HslThemeGeneratorStrategy implements IThemeGeneratorStrategy {

    // #region Fields

    private static readonly _LIGHTNESS_THRESHOLD = 0.5;

    // #endregion

    // #region Methods

    /**
     * Determines whether a color naturally belongs to the given mode's HSL lightness range.
     *
     * @remarks
     * A color with HSL lightness > 0.5 is considered "light"; ≤ 0.5 is considered "dark".
     *
     * @public
     * @param color - The color to check
     * @param mode - The target color mode ('dark' | 'light')
     * @returns `true` if the color is naturally compatible with the given mode
     */
    public isColorInModeSpectrum(color: CssColor, mode: 'dark' | 'light'): boolean {
        const [, , l] = chroma(color).hsl();

        return mode === 'light'
            ? l > HslThemeGeneratorStrategy._LIGHTNESS_THRESHOLD
            : l <= HslThemeGeneratorStrategy._LIGHTNESS_THRESHOLD;
    }

    /**
     * Adapts a color to the opposite mode by symmetrically inverting its HSL lightness
     * (`L → 1 - L`) while preserving hue and saturation.
     *
     * @remarks
     * The inversion is symmetric: a light color becomes dark and vice-versa, regardless
     * of the direction. Therefore no target mode parameter is required.
     *
     * The inverted lightness is clamped to [0.05, 0.95] to avoid pure black / pure white.
     * Subclasses may override this method for theme-specific post-processing.
     *
     * @public
     * @param color - The color to adapt
     * @returns The adapted color with inverted lightness
     */
    public adaptColorToMode(color: CssColor): CssColor {
        const c = chroma(color);
        const [h, s, l] = c.hsl();
        const invertedL = Math.min(Math.max(1 - l, 0.05), 0.95);

        return chroma.hsl(isNaN(h) ? 0 : h, s, invertedL).hex() as CssColor;
    }

    /**
     * Generates a palette of shades based on a base/accent color using HSL interpolation.
     *
     * @public
     * @param baseColor - The base/accent color (e.g. "#3498db")
     * @param mode - 'light' or 'dark' mode: determines how light or dark the palette extremes are.
     * @returns The generated theme palette with shade steps 0–900
     */
    public generatePalette(baseColor: CssColor, mode: 'light' | 'dark'): Required<ThemePalette> {
        const steps = [0, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
        const base = chroma(baseColor);
        const [h, s] = base.hsl();
        const palette: Record<string, CssColor> = {};

        steps.forEach((step, idx) => {
            const t = idx / (steps.length - 1);
            const lightness = mode === 'light'
                ? 0.95 - t * 0.6   // from ~95% light → ~35% light
                : 0.15 + t * 0.6;  // from ~15% light → ~75% light
            const col = chroma.hsl(h, s, lightness);
            palette[step.toString()] = col.hex() as CssColor;
        });

        return {
            "0": palette['0'],
            "50": palette['50'],
            "100": palette['100'],
            "200": palette['200'],
            "300": palette['300'],
            "400": palette['400'],
            "500": palette['500'],
            "600": palette['600'],
            "700": palette['700'],
            "800": palette['800'],
            "900": palette['900']
        };
    }

    /**
     * Generates a semantic color scheme for UI usage, based on the generated palette.
     *
     * @public
     * @param baseColor - The base/accent color
     * @param mode - 'light' or 'dark'
     * @returns The generated theme scheme with semantic color roles
     */
    public generateScheme(baseColor: CssColor, mode: 'light' | 'dark'): Required<ThemeScheme> {
        const p = this.generatePalette(baseColor, mode);

        return {
            background: p['50'],
            foreground: p['900'],
            disabled: p['300'],
            contrast: p['900'],
            highlight: p['700'],
            lowlight: p['300'],
            middlelight: p['500'],
            selection: p['500'],
            semiTransparent: chroma(p['500']).alpha(0.5).hex() as CssColor,
            surface: p['50'],
            transparent: chroma(p['500']).alpha(0).hex() as CssColor,
        };
    }

    // #endregion

}
